import React, { useState, useEffect } from 'react';
import {
  Button,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Typography,
  Box,
  Chip,
  CircularProgress,
  Alert,
  List,
  ListItem,
  ListItemIcon,
  ListItemText,
  Divider,
  Grid,
  Card,
  CardContent,
  CardActions,
} from '@mui/material';
import CreditCardIcon from '@mui/icons-material/CreditCard';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import UpgradeIcon from '@mui/icons-material/Upgrade';
import CancelIcon from '@mui/icons-material/Cancel';
import DeleteIcon from '@mui/icons-material/Delete';
import HistoryIcon from '@mui/icons-material/History';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import ExpandLessIcon from '@mui/icons-material/ExpandLess';
import HourglassEmptyIcon from '@mui/icons-material/HourglassEmpty';
import PrintIcon from '@mui/icons-material/Print';
import OpenInNewIcon from '@mui/icons-material/OpenInNew';
import { useNavigate } from 'react-router-dom';
import { useSubscription } from '../contexts/SubscriptionContext';
import getApiBase from './getApiBase';
import { openVoicePartnerOnboarding } from '../utils/voiceOnboarding';
import { confirmRemoveSubscriptionItem } from '../utils/subscriptionRemoveConfirm';
import { confirmPauseVoice } from '../utils/voicePauseConfirm';
import VoiceNumberReachPanel from './VoiceNumberReachPanel';
import VoiceLineModePanel from './VoiceLineModePanel';

/**
 * TASK-916: hide Voice Pause / Reinstate until aggregate Pause (TASK-915).
 * BE Pause APIs (TASK-914) remain; Cancel & release (Remove) stays visible.
 * Flip to true when TASK-915 ships (one Pause container for Base + extras).
 */
const SHOW_VOICE_PAUSE_UI = false;

const SubscriptionButton = ({ mechanicId, shopId, openBillingModal = false, billingLocked = false }) => {
  const navigate = useNavigate();
  const { getSubscriptionStatus, getPendingPlanIds, updateSubscriptions: updateContextSubscriptions } = useSubscription();
  const [open, setOpen] = useState(false);
  const [historyOpen, setHistoryOpen] = useState(false);
  const [subscriptions, setSubscriptions] = useState([]); // Changed to array
  const [history, setHistory] = useState([]);
  const [hasPaidSubscription, setHasPaidSubscription] = useState(false);
  const [loading, setLoading] = useState(false);
  const [historyLoading, setHistoryLoading] = useState(false);
  const [error, setError] = useState(null);
  const [cancelling, setCancelling] = useState(null); // Track which subscription is being cancelled
  const [openingVoice, setOpeningVoice] = useState(false);
  const [pausingVoice, setPausingVoice] = useState(false);
  const [voicePause, setVoicePause] = useState(null);
  const [voiceDid, setVoiceDid] = useState(null);
  const [voiceNumberReach, setVoiceNumberReach] = useState(null);
  const [hasPublicPhone, setHasPublicPhone] = useState(true);
  const [companyLine, setCompanyLine] = useState(false);
  const [voiceLineMode, setVoiceLineMode] = useState('demo');
  const [voiceLineSaving, setVoiceLineSaving] = useState(false);
  const [voiceLineError, setVoiceLineError] = useState(null);
  const [voiceReachSaving, setVoiceReachSaving] = useState(false);
  const [voiceReachError, setVoiceReachError] = useState(null);
  const pendingPlanIds = getPendingPlanIds();

  const fetchVoicePauseStatus = async () => {
    if (!shopId) return;
    try {
      const apiBase = getApiBase();
      const token = localStorage.getItem('token');
      const res = await fetch(`${apiBase}/api/ezvoice/shops/${shopId}/status`, {
        headers: { Authorization: `Bearer ${token}` },
        credentials: 'include',
      });
      if (!res.ok) return;
      const data = await res.json();
      setVoicePause(data.pause || null);
      setVoiceDid(data.voiceDid || null);
      setVoiceNumberReach(data.voiceNumberReach || null);
      setHasPublicPhone(data.hasPublicPhone !== false);
      setCompanyLine(data.companyLine === true);
      setVoiceLineMode(data.voiceLineMode === 'operations' ? 'operations' : 'demo');
    } catch (err) {
      console.warn('Voice status fetch failed', err);
    }
  };

  const handleVoiceNumberReach = async (next) => {
    if (billingLocked) return;
    if (!shopId || !voiceDid || !hasPublicPhone) return;
    const previous = voiceNumberReach;
    setVoiceNumberReach(next);
    setVoiceReachError(null);
    setVoiceReachSaving(true);
    try {
      const apiBase = getApiBase();
      const token = localStorage.getItem('token');
      const res = await fetch(`${apiBase}/api/ezvoice/shops/${shopId}/number-reach`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          Authorization: `Bearer ${token}`,
        },
        credentials: 'include',
        body: JSON.stringify({ reach: next }),
      });
      const data = await res.json().catch(() => ({}));
      if (!res.ok) {
        setVoiceNumberReach(previous);
        setVoiceReachError(data.error || 'Could not save');
      }
    } catch (err) {
      console.warn('Voice number reach save failed', err);
      setVoiceNumberReach(previous);
      setVoiceReachError('Could not save');
    } finally {
      setVoiceReachSaving(false);
    }
  };

  const handleVoiceLineMode = async (next) => {
    if (billingLocked) return;
    if (!shopId || !companyLine) return;
    const previous = voiceLineMode;
    setVoiceLineMode(next);
    setVoiceLineError(null);
    setVoiceLineSaving(true);
    try {
      const apiBase = getApiBase();
      const token = localStorage.getItem('token');
      const res = await fetch(`${apiBase}/api/ezvoice/shops/${shopId}/line-mode`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          Authorization: `Bearer ${token}`,
        },
        credentials: 'include',
        body: JSON.stringify({ mode: next }),
      });
      const data = await res.json().catch(() => ({}));
      if (!res.ok) {
        setVoiceLineMode(previous);
        setVoiceLineError(data.error || 'Could not save');
      }
    } catch (err) {
      console.warn('Voice line mode save failed', err);
      setVoiceLineMode(previous);
      setVoiceLineError('Could not save');
    } finally {
      setVoiceLineSaving(false);
    }
  };

  const handlePauseVoice = async () => {
    if (billingLocked) return;
    if (!shopId) return;
    if (!confirmPauseVoice({})) return;
    try {
      setPausingVoice(true);
      const apiBase = getApiBase();
      const token = localStorage.getItem('token');
      const res = await fetch(`${apiBase}/api/ezvoice/shops/${shopId}/pause`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          Authorization: `Bearer ${token}`,
        },
        credentials: 'include',
        body: '{}',
      });
      const data = await res.json().catch(() => ({}));
      if (!res.ok) {
        alert(data.error || 'Failed to pause Voice');
        return;
      }
      const ends = data.pauseEndsAt
        ? formatDate(data.pauseEndsAt)
        : 'the Pause deadline';
      alert(
        `Voice paused. Your number is kept. Reinstate before ${ends} or the number will be Cancelled & released. Billing continues while paused.`,
      );
      await fetchVoicePauseStatus();
      await fetchSubscription();
    } catch (err) {
      console.error('Pause Voice failed', err);
      alert('Failed to pause Voice');
    } finally {
      setPausingVoice(false);
    }
  };

  const handleResumeVoice = async () => {
    if (billingLocked) return;
    if (!shopId) return;
    try {
      setPausingVoice(true);
      const apiBase = getApiBase();
      const token = localStorage.getItem('token');
      const res = await fetch(`${apiBase}/api/ezvoice/shops/${shopId}/resume`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          Authorization: `Bearer ${token}`,
        },
        credentials: 'include',
        body: '{}',
      });
      const data = await res.json().catch(() => ({}));
      if (!res.ok) {
        alert(
          data.error ||
            'Reinstate after Pause is not available yet (EasyVoice resume pending).',
        );
        return;
      }
      alert('Voice reinstated.');
      await fetchVoicePauseStatus();
      await fetchSubscription();
    } catch (err) {
      console.error('Resume Voice failed', err);
      alert('Failed to reinstate Voice');
    } finally {
      setPausingVoice(false);
    }
  };

  const handleOpenVoiceSetup = async () => {
    if (billingLocked) return;
    try {
      setOpeningVoice(true);
      await openVoicePartnerOnboarding(shopId);
    } catch (err) {
      alert(err.message || 'Could not open Voice setup');
    } finally {
      setOpeningVoice(false);
    }
  };

  // Fetch subscription data on mount AND when modal opens
  useEffect(() => {
    fetchSubscription();
  }, [mechanicId, shopId]);

  // Also refresh when modal opens (in case data changed)
  useEffect(() => {
    if (open) {
      fetchSubscription();
      fetchVoicePauseStatus();
    }
  }, [open]);

  useEffect(() => {
    if (openBillingModal) {
      setOpen(true);
      fetchSubscription();
    }
  }, [openBillingModal]);

  useEffect(() => {
    if (!open || !openBillingModal) return;
    const panel = document.getElementById('voice-number-reach');
    if (panel) panel.scrollIntoView({ block: 'nearest' });
  }, [open, openBillingModal, voiceDid]);

  const fetchSubscription = async () => {
    try {
      const apiBase = getApiBase();
      setLoading(true);
      setError(null);
      const token = localStorage.getItem('token');

      const response = await fetch(
        `${apiBase}/api/mechanicProfile/subscription?mechanic_id=${mechanicId}&shop_id=${shopId}`,
        {
          headers: {
            Authorization: `Bearer ${token}`,
          },
          credentials: 'include',
        }
      );

      if (response.ok) {
        const data = await response.json();
        setSubscriptions(data.subscriptions || []);
        setHasPaidSubscription(data.hasPaidSubscription || false);
        
        // Update context with latest subscriptions
        updateContextSubscriptions(data.subscriptions || []);
      } else {
        setError('Failed to load subscription information');
      }
    } catch (err) {
      console.error('Error fetching subscription:', err);
      setError('Failed to load subscription information');
    } finally {
      setLoading(false);
    }
  };

  const fetchHistory = async () => {
    try {
      const apiBase = getApiBase();
      setHistoryLoading(true);
      const token = localStorage.getItem('token');

      const response = await fetch(
        `${apiBase}/api/mechanicProfile/subscription-history?mechanic_id=${mechanicId}&shop_id=${shopId}`,
        {
          headers: {
            Authorization: `Bearer ${token}`,
          },
          credentials: 'include',
        }
      );

      if (response.ok) {
        const data = await response.json();
        console.log('📊 Subscription history response:', data);
        setHistory(data.subscriptions || []); // Get subscription history
      } else {
        const errorData = await response.json().catch(() => ({}));
        console.error('❌ Failed to fetch billing history:', response.status, errorData);
      }
    } catch (err) {
      console.error('Error fetching payment history:', err);
    } finally {
      setHistoryLoading(false);
    }
  };

  const handleUpgrade = () => {
    if (billingLocked) return;
    setOpen(false);
    navigate('/pricing');
  };

  const handleCancelSubscription = async (subscriptionId) => {
    if (billingLocked) return;
    // ⚠️ CONFIRMATION PROMPT - Prevent accidental cancellation
    const confirmed = window.confirm(
      '⚠️ Are you sure you want to cancel this subscription?\n\n' +
      'Your subscription will remain active until the end of the current billing period.\n\n' +
      'Click OK to confirm cancellation, or Cancel to keep your subscription.'
    );

    if (!confirmed) {
      return; // User clicked "Cancel" - do nothing
    }

    try {
      const apiBase = getApiBase();
      setCancelling(subscriptionId);
      const token = localStorage.getItem('token');

      const response = await fetch(
        `${apiBase}/api/stripe/cancel-subscription`,
        {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
            Authorization: `Bearer ${token}`,
          },
          credentials: 'include',
          body: JSON.stringify({
            subscriptionId: subscriptionId,
          }),
        }
      );

      if (response.ok) {
        const data = await response.json();
        alert(data.message || 'Subscription cancelled successfully');
        // Poll for DB update (webhook takes a moment)
        setTimeout(() => fetchSubscription(), 2000);
      } else {
        const data = await response.json();
        alert(data.error || 'Failed to cancel subscription');
      }
    } catch (err) {
      console.error('Error cancelling subscription:', err);
      alert('Failed to cancel subscription');
    } finally {
      setCancelling(null);
    }
  };

  const handleReinstateSubscription = async (subscriptionId, planName) => {
    if (billingLocked) return;
    if (!window.confirm(`Are you sure you want to reinstate "${planName}"? Your subscription will continue as normal.`)) {
      return;
    }

    try {
      const apiBase = getApiBase();
      setCancelling(subscriptionId);
      const token = localStorage.getItem('token');

      const response = await fetch(
        `${apiBase}/api/stripe/reinstate-subscription`,
        {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
            Authorization: `Bearer ${token}`,
          },
          credentials: 'include',
          body: JSON.stringify({
            subscriptionId: subscriptionId,
          }),
        }
      );

      const data = await response.json();

      if (response.ok) {
        alert(data.message || 'Subscription reinstated successfully');
        fetchSubscription(); // Refresh subscription data
      } else {
        alert(data.error || 'Failed to reinstate subscription');
      }
    } catch (err) {
      console.error('Error reinstating subscription:', err);
      alert('Failed to reinstate subscription');
    } finally {
      setCancelling(null);
    }
  };

  const handleRemoveItem = async (subscriptionId, planName, planIsActive = true) => {
    if (billingLocked) return;
    // ⚠️ CONFIRMATION PROMPT - Prevent accidental removal
    // Find the subscription to get period end date
    const subscription = subscriptions.find(sub => sub.id === subscriptionId);
    const periodEndDate = subscription?.current_period_end 
      ? formatDate(subscription.current_period_end)
      : 'the end of your billing period';
    const isVoiceBase = subscription?.plan?.name === 'addon_ai_voice';
    
    const confirmed = confirmRemoveSubscriptionItem({
      planName,
      periodEndDate,
      isVoiceBase,
      planIsActive,
    });

    if (!confirmed) {
      return;
    }

    try {
      const apiBase = getApiBase();
      setCancelling(subscriptionId);
      const token = localStorage.getItem('token');

      const response = await fetch(
        `${apiBase}/api/stripe/remove-subscription-item`,
        {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
            Authorization: `Bearer ${token}`,
          },
          credentials: 'include',
          body: JSON.stringify({
            subscriptionId: subscriptionId,
          }),
        }
      );

      if (response.ok) {
        const data = await response.json();
        alert(data.message || 'Subscription item removed successfully');
        // Refresh immediately (preview often has no Stripe webhooks)
        await fetchSubscription();
        setTimeout(() => fetchSubscription(), 1500);
      } else {
        const data = await response.json();
        alert(data.error || 'Failed to remove subscription item');
      }
    } catch (err) {
      console.error('Error removing subscription item:', err);
      alert('Failed to remove subscription item');
    } finally {
      setCancelling(null);
    }
  };

  const getStatusColor = (status) => {
    switch (status) {
      case 'active':
        return 'success';
      case 'trialing':
        return 'info';
      case 'past_due':
        return 'warning';
      case 'cancelled':
        return 'error';
      default:
        return 'default';
    }
  };

  const getPaymentStatusColor = (status) => {
    switch (status) {
      case 'succeeded':
        return 'success';
      case 'pending':
        return 'info';
      case 'failed':
        return 'error';
      case 'refunded':
        return 'warning';
      default:
        return 'default';
    }
  };

  const formatCurrency = (amount, currency = 'USD') => {
    return new Intl.NumberFormat('en-US', {
      style: 'currency',
      currency: currency.toUpperCase(),
    }).format(amount);
  };

  const formatDate = (dateString) => {
    if (!dateString) return 'N/A';
    return new Date(dateString).toLocaleDateString('en-US', {
      year: 'numeric',
      month: 'long',
      day: 'numeric',
    });
  };

  // Calculate next billing date from created_at and billing_cycle if current_period_end is missing
  const getNextBillingDate = (subscription) => {
    // If we have current_period_end, use it
    if (subscription.current_period_end) {
      return subscription.current_period_end;
    }
    
    // Fallback 1: Calculate from current_period_start and billing_cycle
    if (subscription.current_period_start) {
      try {
        const periodStart = new Date(subscription.current_period_start);
        if (!isNaN(periodStart.getTime())) {
          const nextBilling = new Date(periodStart);
          const billingCycle = subscription.billing_cycle || 'monthly'; // Default to monthly if missing
          
          if (billingCycle === 'yearly') {
            nextBilling.setFullYear(nextBilling.getFullYear() + 1);
          } else {
            nextBilling.setMonth(nextBilling.getMonth() + 1);
          }
          
          return nextBilling.toISOString();
        }
      } catch (e) {
        console.warn('Error calculating billing date from current_period_start:', e);
      }
    }
    
    // Fallback 2: Calculate from created_at and billing_cycle (handle both null and undefined)
    const createdAt = subscription.created_at;
    if (createdAt !== null && createdAt !== undefined) {
      try {
        const createdDate = new Date(createdAt);
        if (!isNaN(createdDate.getTime())) {
          const nextBilling = new Date(createdDate);
          const billingCycle = subscription.billing_cycle || 'monthly'; // Default to monthly if missing
          
          if (billingCycle === 'yearly') {
            nextBilling.setFullYear(nextBilling.getFullYear() + 1);
          } else {
            nextBilling.setMonth(nextBilling.getMonth() + 1);
          }
          
          return nextBilling.toISOString();
        }
      } catch (e) {
        console.warn('Error calculating billing date from created_at:', e);
      }
    }
    
    // Fallback 3: Use today's date + billing cycle (last resort - at least show something)
    // This gives a reasonable estimate when no date fields are available
    try {
      const today = new Date();
      const nextBilling = new Date(today);
      const billingCycle = subscription.billing_cycle || 'monthly'; // Default to monthly if missing
      
      if (billingCycle === 'yearly') {
        nextBilling.setFullYear(nextBilling.getFullYear() + 1);
      } else {
        nextBilling.setMonth(nextBilling.getMonth() + 1);
      }
      
      console.warn('Using estimated billing date (today + billing cycle) for subscription:', subscription.id);
      return nextBilling.toISOString();
    } catch (e) {
      console.error('Error calculating estimated billing date:', e);
    }
    
    // If all else fails, return null (will show N/A)
    return null;
  };

  // Free/base tiers are included with the shop — do not count them as paid plans.
  // Matches BE hasPaidSubscription and the modal (bid_tier + addon only).
  const paidSubscriptions = subscriptions.filter((sub) => {
    const plan = sub.plan;
    if (!plan) return false;
    if (plan.plan_type === 'base_tier' || plan.name === 'free_base') return false;
    return parseFloat(plan.monthly_price) > 0;
  });

  // Get button label based on paid subscriptions only
  const getButtonLabel = () => {
    if (loading && subscriptions.length === 0) return 'Loading...';
    if (!hasPaidSubscription || paidSubscriptions.length === 0) return 'Free Plan';
    if (paidSubscriptions.length === 1) {
      return `${paidSubscriptions[0].plan?.display_name || 'Active Plan'}`;
    }
    return `${paidSubscriptions.length} Active Plans`;
  };

  return (
    <>
      {/* Compact Button */}
      <Button
        variant="outlined"
        startIcon={<CreditCardIcon />}
        onClick={() => setOpen(true)}
        sx={{
          color: '#fff',
          borderRadius: 2,
          boxShadow: 3,
          backgroundColor: '#34a853',
          borderColor: '#34a853',
          transition: '0.3s',
          '&:hover': {
            transform: 'scale(1.03)',
            backgroundColor: '#2d8e47',
            borderColor: '#2d8e47',
          },
        }}
      >
        {getButtonLabel()}
      </Button>

      {/* Subscription Details Modal - List View */}
      <Dialog open={open} onClose={() => setOpen(false)} maxWidth="md" fullWidth>
        <DialogTitle>
          <Box display="flex" justifyContent="space-between" alignItems="center">
            <Typography variant="h5" fontWeight="bold">
              Your Active Subscriptions
            </Typography>
          </Box>
        </DialogTitle>

        <DialogContent>
          {loading ? (
            <Box display="flex" justifyContent="center" p={3}>
              <CircularProgress />
            </Box>
          ) : error ? (
            <Alert severity="error">{error}</Alert>
          ) : (
            <>
              {/* Free Plan Message */}
              {(!hasPaidSubscription || subscriptions.length === 0) && (
                <Box mb={3}>
                  <Typography variant="h6" color="text.secondary" gutterBottom>
                    You are currently on the Free plan
                  </Typography>
                  <Typography variant="body2" color="text.secondary">
                    Upgrade to access premium features and increase your bid capacity.
                  </Typography>
                </Box>
              )}

              {/* Subscription List View Grouped by Category */}
              {subscriptions.length > 0 && (
                <>
                  {/* BID TIER Section */}
                  {subscriptions.filter(sub => sub.plan?.plan_type === 'bid_tier').length > 0 && (
                    <Box mb={3}>
                      <Typography variant="overline" fontWeight="bold" color="text.secondary" display="block" mb={1}>
                        BID TIER
                      </Typography>
                      <List dense>
                        {subscriptions.filter(sub => sub.plan?.plan_type === 'bid_tier').map((sub) => {
                          const plan = sub.plan;
                          const price = sub.billing_cycle === 'yearly' ? plan?.yearly_price : plan?.monthly_price;
                          const cycle = sub.billing_cycle === 'yearly' ? 'year' : 'month';
                          
                          return (
                            <ListItem
                              key={sub.id}
                              sx={{
                                bgcolor: 'background.paper',
                                mb: 1,
                                border: 1,
                                borderColor: sub.cancel_at_period_end === 1 ? 'warning.main' : 'divider',
                                borderRadius: 1,
                              }}
                            >
                              <ListItemIcon sx={{ minWidth: 40 }}>
                                <CheckCircleIcon color="success" />
                              </ListItemIcon>
                              <ListItemText
                                primary={
                                  <Box display="flex" alignItems="center" gap={1}>
                                    <Typography variant="body1" fontWeight="bold">
                                      {plan?.display_name || 'Unknown Plan'}
                                    </Typography>
                                    {plan?.is_active === 0 && (
                                      <Chip
                                        label="NO LONGER AVAILABLE"
                                        color="warning"
                                        size="small"
                                      />
                                    )}
                                    <Chip
                                      label={
                                        sub.cancel_at_period_end === 1
                                          ? 'CANCELING'
                                          : sub.status.toUpperCase()
                                      }
                                      color={
                                        sub.cancel_at_period_end === 1
                                          ? 'warning'
                                          : getStatusColor(sub.status)
                                      }
                                      size="small"
                                    />
                                  </Box>
                                }
                                secondary={
                                  <>
                                    {plan?.is_active === 0 && (
                                      <Typography variant="body2" color="warning.main" component="span" display="block" fontWeight="bold">
                                        ⚠️ This plan is no longer available. If cancelled, it cannot be re-added.
                                      </Typography>
                                    )}
                                    <Typography variant="body2" component="span">
                                      Next billing: {formatDate(getNextBillingDate(sub))}
                                    </Typography>
                                    {sub.cancel_at_period_end === 1 && (
                                      <Typography variant="body2" color="warning.main" component="span" display="block">
                                        ⚠️ Cancels on {formatDate(sub.current_period_end)}
                                      </Typography>
                                    )}
                                  </>
                                }
                              />
                              <Box display="flex" alignItems="center" gap={2}>
                                <Typography variant="h6" color="primary" fontWeight="bold">
                                  ${price}/{cycle}
                                </Typography>
                                {sub.cancel_at_period_end === 1 ? (
                                  <Button
                                    size="small"
                                    variant="contained"
                                    color="success"
                                    startIcon={cancelling === sub.id ? null : <CheckCircleIcon />}
                                    onClick={() => handleReinstateSubscription(sub.id, plan?.display_name)}
                                    disabled={cancelling === sub.id}
                                  >
                                    {cancelling === sub.id ? <CircularProgress size={20} /> : 'Reinstate'}
                                  </Button>
                                ) : (
                                  <Button
                                    size="small"
                                    variant="outlined"
                                    color="error"
                                    startIcon={cancelling === sub.id ? null : <DeleteIcon />}
                                    onClick={() => handleRemoveItem(sub.id, plan?.display_name, plan?.is_active !== 0)}
                                    disabled={cancelling === sub.id}
                                  >
                                    {cancelling === sub.id ? <CircularProgress size={20} /> : 'Remove'}
                                  </Button>
                                )}
                              </Box>
                            </ListItem>
                          );
                        })}
                      </List>
                    </Box>
                  )}

                  {/* ADD-ONS Section */}
                  {subscriptions.filter(sub => sub.plan?.plan_type === 'addon').length > 0 && (
                    <Box mb={3}>
                      <Typography variant="overline" fontWeight="bold" color="text.secondary" display="block" mb={1}>
                        ADD-ONS
                      </Typography>
                      <List dense>
                        {subscriptions.filter(sub => sub.plan?.plan_type === 'addon').map((sub) => {
                          const plan = sub.plan;
                          const price = sub.billing_cycle === 'yearly' ? plan?.yearly_price : plan?.monthly_price;
                          const cycle = sub.billing_cycle === 'yearly' ? 'year' : 'month';
                          
                          return (
                            <ListItem
                              key={sub.id}
                              sx={{
                                bgcolor: 'background.paper',
                                mb: 1,
                                border: 1,
                                borderColor: sub.cancel_at_period_end === 1 ? 'warning.main' : 'divider',
                                borderRadius: 1,
                              }}
                            >
                              <ListItemIcon sx={{ minWidth: 40 }}>
                                <CheckCircleIcon color="success" />
                              </ListItemIcon>
                              <ListItemText
                                primary={
                                  <Box display="flex" alignItems="center" gap={1}>
                                    <Typography variant="body1" fontWeight="bold">
                                      {plan?.display_name || 'Unknown Plan'}
                                    </Typography>
                                    {plan?.is_active === 0 && (
                                      <Chip
                                        label="NO LONGER AVAILABLE"
                                        color="warning"
                                        size="small"
                                      />
                                    )}
                                    <Chip
                                      label={
                                        SHOW_VOICE_PAUSE_UI &&
                                        plan?.name === 'addon_ai_voice' &&
                                        voicePause?.paused
                                          ? 'PAUSED'
                                          : sub.cancel_at_period_end === 1
                                          ? 'CANCELING'
                                          : sub.status.toUpperCase()
                                      }
                                      color={
                                        SHOW_VOICE_PAUSE_UI &&
                                        plan?.name === 'addon_ai_voice' &&
                                        voicePause?.paused
                                          ? 'info'
                                          : sub.cancel_at_period_end === 1
                                          ? 'warning'
                                          : getStatusColor(sub.status)
                                      }
                                      size="small"
                                    />
                                  </Box>
                                }
                                secondary={
                                  <>
                                    {plan?.is_active === 0 && (
                                      <Typography variant="body2" color="warning.main" component="span" display="block" fontWeight="bold">
                                        ⚠️ This plan is no longer available. If cancelled, it cannot be re-added.
                                      </Typography>
                                    )}
                                    {SHOW_VOICE_PAUSE_UI &&
                                      plan?.name === 'addon_ai_voice' &&
                                      voicePause?.paused &&
                                      voicePause?.voicePauseEndsAt && (
                                      <Typography variant="body2" color="info.main" component="span" display="block">
                                        Voice paused — Reinstate by {formatDate(voicePause.voicePauseEndsAt)} or the number is Cancelled & released. Billing continues.
                                      </Typography>
                                    )}
                                    <Typography variant="body2" component="span">
                                      Next billing: {formatDate(getNextBillingDate(sub))}
                                    </Typography>
                                    {sub.cancel_at_period_end === 1 && (
                                      <Typography variant="body2" color="warning.main" component="span" display="block">
                                        ⚠️ Cancels on {formatDate(sub.current_period_end)}
                                      </Typography>
                                    )}
                                  </>
                                }
                              />
                              <Box display="flex" alignItems="center" gap={1} flexWrap="wrap">
                                <Typography variant="h6" color="primary" fontWeight="bold">
                                  ${price}/{cycle}
                                </Typography>
                                {plan?.name === 'addon_ai_voice' &&
                                  sub.cancel_at_period_end !== 1 &&
                                  (sub.status === 'active' || sub.status === 'trialing') &&
                                  !voicePause?.paused && (
                                  <Button
                                    size="small"
                                    variant="contained"
                                    color="primary"
                                    onClick={handleOpenVoiceSetup}
                                    disabled={openingVoice}
                                    sx={{ textTransform: 'none' }}
                                  >
                                    {openingVoice ? <CircularProgress size={18} /> : 'Complete Voice setup'}
                                  </Button>
                                )}
                                {SHOW_VOICE_PAUSE_UI &&
                                  plan?.name === 'addon_ai_voice' &&
                                  sub.cancel_at_period_end !== 1 &&
                                  (sub.status === 'active' || sub.status === 'trialing') &&
                                  !voicePause?.paused && (
                                  <Button
                                    size="small"
                                    variant="outlined"
                                    color="warning"
                                    onClick={handlePauseVoice}
                                    disabled={pausingVoice}
                                    sx={{ textTransform: 'none' }}
                                  >
                                    {pausingVoice ? <CircularProgress size={18} /> : 'Pause Voice'}
                                  </Button>
                                )}
                                {SHOW_VOICE_PAUSE_UI &&
                                  plan?.name === 'addon_ai_voice' &&
                                  voicePause?.paused &&
                                  sub.cancel_at_period_end !== 1 && (
                                  <Button
                                    size="small"
                                    variant="contained"
                                    color="success"
                                    onClick={handleResumeVoice}
                                    disabled={pausingVoice}
                                    sx={{ textTransform: 'none' }}
                                  >
                                    {pausingVoice ? <CircularProgress size={18} /> : 'Reinstate Voice'}
                                  </Button>
                                )}
                                {sub.cancel_at_period_end === 1 ? (
                                  <Button
                                    size="small"
                                    variant="contained"
                                    color="success"
                                    startIcon={cancelling === sub.id ? null : <CheckCircleIcon />}
                                    onClick={() => handleReinstateSubscription(sub.id, plan?.display_name)}
                                    disabled={cancelling === sub.id}
                                  >
                                    {cancelling === sub.id ? <CircularProgress size={20} /> : 'Reinstate'}
                                  </Button>
                                ) : (
                                  <Button
                                    size="small"
                                    variant="outlined"
                                    color="error"
                                    startIcon={cancelling === sub.id ? null : <DeleteIcon />}
                                    onClick={() => handleRemoveItem(sub.id, plan?.display_name, plan?.is_active !== 0)}
                                    disabled={cancelling === sub.id}
                                  >
                                    {cancelling === sub.id ? <CircularProgress size={20} /> : 'Remove'}
                                  </Button>
                                )}
                              </Box>
                            </ListItem>
                          );
                        })}
                      </List>
                    </Box>
                  )}

                  {subscriptions.some(
                    (sub) =>
                      sub.plan?.name === 'addon_ai_voice' &&
                      sub.cancel_at_period_end !== 1 &&
                      (sub.status === 'active' || sub.status === 'trialing'),
                  ) && (
                    <>
                      {companyLine && (
                        <VoiceLineModePanel
                          mode={voiceLineMode}
                          saving={voiceLineSaving}
                          error={voiceLineError}
                          onChange={handleVoiceLineMode}
                        />
                      )}
                      <VoiceNumberReachPanel
                        voiceDid={voiceDid}
                        reach={voiceNumberReach}
                        hasPublicPhone={hasPublicPhone}
                        saving={voiceReachSaving}
                        error={voiceReachError}
                        onChange={handleVoiceNumberReach}
                      />
                    </>
                  )}

                  <Divider sx={{ my: 2 }} />

                  {/* Subscription monthly total */}
                  <Box sx={{ bgcolor: 'grey.50', p: 2, borderRadius: 1 }}>
                    <Box display="flex" justifyContent="space-between" alignItems="center" mb={1}>
                      <Typography variant="body1" fontWeight="bold">
                        Total Monthly Cost:
                      </Typography>
                      <Typography variant="h5" color="primary" fontWeight="bold">
                        ${subscriptions.reduce((total, sub) => {
                          if (sub.cancel_at_period_end === 1) return total;
                          const price = parseFloat(sub.billing_cycle === 'yearly' ? sub.plan?.yearly_price / 12 : sub.plan?.monthly_price);
                          return total + (isNaN(price) ? 0 : price);
                        }, 0).toFixed(2)}/month
                      </Typography>
                    </Box>
                    {subscriptions.length > 0 && getNextBillingDate(subscriptions[0]) && (
                      <Typography variant="caption" color="text.secondary">
                        Next billing date: {formatDate(getNextBillingDate(subscriptions[0]))}
                      </Typography>
                    )}
                  </Box>
                </>
              )}
            </>
          )}
        </DialogContent>

        <DialogActions sx={{ p: 2, gap: 1 }}>
          <Button 
            onClick={() => {
              setHistoryOpen(true);
              fetchHistory();
            }} 
            startIcon={<HistoryIcon />}
            color="inherit"
          >
            View History
          </Button>
          
          <Box sx={{ flexGrow: 1 }} />
          
          <Button onClick={() => setOpen(false)} color="inherit">
            Close
          </Button>
          
          {!loading && !error && (
            <Button
              variant="contained"
              color="primary"
              startIcon={<UpgradeIcon />}
              onClick={handleUpgrade}
              disabled={billingLocked}
            >
              {hasPaidSubscription ? 'Add More Features' : 'Upgrade Plan'}
            </Button>
          )}
        </DialogActions>
      </Dialog>

      {/* Subscription History Modal */}
      <Dialog open={historyOpen} onClose={() => setHistoryOpen(false)} maxWidth="md" fullWidth>
        <DialogTitle>
          <Typography variant="h5" fontWeight="bold">
            Subscription History
          </Typography>
        </DialogTitle>

        <DialogContent>
          {historyLoading ? (
            <Box display="flex" justifyContent="center" p={3}>
              <CircularProgress />
            </Box>
          ) : history.length === 0 ? (
            <Alert severity="info">No subscription history found</Alert>
          ) : (
            <Box>
              <List>
                {history.map((sub) => {
                  const price = sub.billing_cycle === 'yearly' 
                    ? (sub.price / 12).toFixed(2) 
                    : sub.price;
                  
                  const getStatusColor = (status, isActive) => {
                    if (isActive) {
                      if (status === 'active') return 'success';
                      if (status === 'trialing') return 'info';
                      if (status === 'past_due') return 'warning';
                    }
                    return 'default';
                  };
                  
                  const getStatusLabel = (status, cancelAtPeriodEnd, endDate) => {
                    if (cancelAtPeriodEnd && endDate && new Date(endDate) > new Date()) {
                      return 'Cancelling';
                    }
                    if (status === 'active') return 'Active';
                    if (status === 'trialing') return 'Trial';
                    if (status === 'past_due') return 'Past Due';
                    if (status === 'cancelled') return 'Cancelled';
                    return status;
                  };
                  
                  const statusColor = getStatusColor(sub.status, sub.is_active);
                  const statusLabel = getStatusLabel(sub.status, sub.cancel_at_period_end, sub.current_period_end);

                  return (
                    <ListItem key={sub.id} sx={{ mb: 2, border: 1, borderColor: 'divider', borderRadius: 1 }}>
                      <ListItemIcon>
                        {sub.is_active ? (
                          <CheckCircleIcon color={statusColor} />
                        ) : (
                          <CancelIcon color="default" />
                        )}
                          </ListItemIcon>
                          <ListItemText
                            primary={
                          <Box display="flex" justifyContent="space-between" alignItems="center">
                            <Typography variant="h6" fontWeight="bold">
                              {sub.plan_name}
                                  </Typography>
                            <Box display="flex" alignItems="center" gap={1}>
                                  <Chip
                                label={statusLabel} 
                                color={statusColor} 
                                    size="small"
                                  />
                              <Typography variant="h6" fontWeight="bold">
                                {formatCurrency(price)}
                                  </Typography>
                                </Box>
                              </Box>
                            }
                            secondary={
                          <Box sx={{ mt: 1 }}>
                            <Typography variant="body2" color="text.secondary">
                              <strong>Started:</strong> {formatDate(sub.start_date)}
                              {sub.end_date && (
                                <> | <strong>Ended:</strong> {formatDate(sub.end_date)}</>
                              )}
                                  </Typography>
                            {sub.current_period_start && sub.current_period_end && (
                              <Typography variant="body2" color="text.secondary">
                                <strong>Current Period:</strong> {formatDate(sub.current_period_start)} - {formatDate(sub.current_period_end)}
                                  </Typography>
                                )}
                            {sub.cancel_at_period_end && sub.current_period_end && (
                              <Typography variant="body2" color="warning.main">
                                <strong>Cancels on:</strong> {formatDate(sub.current_period_end)}
                                  </Typography>
                                )}
                            <Typography variant="body2" color="text.secondary">
                              <strong>Billing Cycle:</strong> {sub.billing_cycle === 'yearly' ? 'Yearly' : 'Monthly'} | 
                              <strong> Plan Type:</strong> {sub.plan_type}
                                  </Typography>
                                  </Box>
                            }
                          />
                        </ListItem>
                  );
                })}
              </List>
            </Box>
          )}
        </DialogContent>

        <DialogActions sx={{ p: 2 }}>
          <Button onClick={() => setHistoryOpen(false)} color="inherit">
            Close
          </Button>
        </DialogActions>
      </Dialog>
    </>
  );
};

export default SubscriptionButton;

